import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  LayoutDashboard,
  Database,
  BookOpen,
  CreditCard,
  Users,
  CalendarDays,
  Upload,
  BarChart2,
  Settings,
  LogOut,
  X,
  GraduationCap,
  ClipboardList,
  Video,
  Film,
  Layers,
  Megaphone,
  MessagesSquare,
} from "lucide-react";
import { logoutUserThunk } from "../../../auth/authThunk";
import "../lms.css";

const BASE_NAV = [
  {
    section: "MAIN MENU",
    items: [
      { label: "Dashboard",          icon: LayoutDashboard, to: "/lms",               countKey: null,      badge: null },
      { label: "Master Setup",       icon: Database,        to: "/lms/master-setup",  countKey: null,      badge: "New" },
      { label: "Courses",            icon: BookOpen,        to: "/lms/courses",        countKey: "courses", badge: null },
      { label: "Payment Config",     icon: CreditCard,      to: "/lms/payment-config", countKey: null,      badge: null },
      { label: "Students",           icon: Users,           to: "/lms/students",       countKey: null,      badge: null },
      { label: "Teachers",           icon: GraduationCap,   to: "/lms/teachers",       countKey: null,      badge: null },
      // { label: "Classes & Schedule", icon: CalendarDays,    to: "/lms/classes",        countKey: null,      badge: null },
      { label: "Exams",              icon: ClipboardList,   to: "/lms/exams",           countKey: null,      badge: null },
      { label: "Live Classes",       icon: Video,           to: "/lms/live-classes",    countKey: null,      badge: null },
      { label: "Recordings",         icon: Film,            to: "/lms/recordings",       countKey: null,      badge: null },
      { label: "Audit Log",          icon: ClipboardList,   to: "/lms/audit-log",       countKey: null,      badge: null },
      { label: "Attendance",         icon: ClipboardList,   to: "/lms/attendance",      countKey: null,      badge: null },
      { label: "Batches",            icon: Layers,          to: "/lms/batches",          countKey: null,      badge: null },
      {
        label: "Communications",
        icon: MessagesSquare,
        children: [
          { label: "Notice Board/Chat", icon: Megaphone, to: "/lms/notice-board" },
        ],
      },
    ],
  },
  {
    section: "MANAGEMENT",
    items: [
      // { label: "Upload Center",       icon: Upload,   to: "/lms/upload",   countKey: null, badge: null },
      // { label: "Reports & Analytics", icon: BarChart2, to: "/lms/reports",  countKey: null, badge: null },
      // { label: "System Settings",     icon: Settings, to: "/lms/settings", countKey: null, badge: null },
    ],
  },
];

function getInitials(name = "") {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function LmsSidebar({ isOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { courses } = useSelector((s) => s.lms);
  const counts = { courses: courses.length || null };

  const handleLogout = async () => {
    await dispatch(logoutUserThunk());
    localStorage.removeItem("userData");
    localStorage.removeItem("userToken");
    navigate("/login");
  };

  // Read user from localStorage
  const userData = (() => {
    try {
      return JSON.parse(localStorage.getItem("userData") || "{}");
    } catch {
      return {};
    }
  })();

  const userName  = userData?.admin_name  || "Admin User";
  const schoolName = userData?.school_name || "";
  const userRole  = userData?.roles || userData?.role || "Administrator";

  const handleNavClick = () => {
    // Close sidebar on mobile when a nav item is clicked
    if (onClose) onClose();
  };

  return (
    <aside className={`lms-sidebar ${isOpen ? "lms-sidebar--open" : ""}`}>
      {/* Logo + mobile close */}
      <div className="lms-sidebar-logo">
        <div className="lms-logo-icon">D</div>
        <div className="lms-logo-text">
          <span className="lms-logo-title">DocMaster LMS</span>
          <span className="lms-logo-sub">ADMIN</span>
        </div>
        {/* Close button — mobile only */}
        <button className="lms-sidebar-close" onClick={onClose} title="Close menu">
          <X size={16} strokeWidth={2} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="lms-sidebar-nav">
        {BASE_NAV.map((group) => (
          <div key={group.section}>
            <div className="lms-nav-section-label">{group.section}</div>
            {group.items.map((item) => {
              const Icon = item.icon;

              if (item.children) {
                return (
                  <div key={item.label}>
                    <div className="lms-nav-item" style={{ cursor: "default", opacity: 0.85 }}>
                      <span className="lms-nav-icon">
                        <Icon size={16} strokeWidth={2} />
                      </span>
                      <span className="lms-nav-label">{item.label}</span>
                    </div>
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const childActive = location.pathname.startsWith(child.to);
                      return (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          className={`lms-nav-item ${childActive ? "active" : ""}`}
                          onClick={handleNavClick}
                          style={{ paddingLeft: "34px" }}
                        >
                          <span className="lms-nav-icon">
                            <ChildIcon size={14} strokeWidth={2} />
                          </span>
                          <span className="lms-nav-label">{child.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                );
              }

              const isActive =
                item.to === "/lms"
                  ? location.pathname === "/lms" || location.pathname === "/lms/"
                  : location.pathname.startsWith(item.to);
              const count = item.countKey ? counts[item.countKey] : null;

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={`lms-nav-item ${isActive ? "active" : ""}`}
                  end={item.to === "/lms"}
                  onClick={handleNavClick}
                >
                  <span className="lms-nav-icon">
                    <Icon size={16} strokeWidth={2} />
                  </span>
                  <span className="lms-nav-label">{item.label}</span>
                  {item.badge === "New" && (
                    <span className="lms-nav-badge-new">New</span>
                  )}
                  {count != null && (
                    <span className="lms-nav-count">{count}</span>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User info */}
      <div className="lms-sidebar-user">
        <div className="lms-user-avatar">{getInitials(userName)}</div>
        <div className="lms-user-info">
          <div className="lms-user-name">{userName}</div>
          <div className="lms-user-role">{userRole}</div>
          {schoolName && (
            <div className="lms-user-school" title={schoolName}>{schoolName}</div>
          )}
        </div>
        <button className="lms-logout-btn" onClick={handleLogout} title="Logout">
          <LogOut size={15} strokeWidth={2} />
        </button>
      </div>
    </aside>
  );
}
